import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { interviewRuntimeAPI } from '../api/interviewRuntimeAPI';
import InterviewUpgradeGate, { useGateStatus } from './InterviewUpgradeGate';
import { holdsPaidExam } from '../../quiz-generation/components/LockedPartNotice';
import {
  clearDraft, clearResult, loadResult, writtenProgress,
} from '../writtenStorage';
import './InterviewBrowse.css';

/**
 * A mock interview paper's brief: one card per session. It is taken in
 * writing, like an exam paper; the spoken interview is the separate "Start
 * live interview" on the course page, drawn fresh each time, so a student
 * never has to pick a paper to be interviewed.
 *
 * A session is the sitting, measured against the real corpus: Medicine and
 * Dentistry papers carry one session (~5 questions, the paper's full 60
 * minutes), Oxbridge papers two (~2 questions, ~30 minutes each). Every
 * question is offered, including the 31 in the corpus with no follow-ups:
 * written, those are marked on the answer to the question itself.
 */
export default function InterviewPaperBriefPage() {
  const { workspace: workspaceSlug, leafKey, paperId } = useParams();
  // Saved progress is the signed-in user's own; without an id there is none.
  const { user } = useAuth();
  const userId = user?.id || null;
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [gateCode, statusFor] = useGateStatus();
  const [paper, setPaper] = useState(null);
  const [, setRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    interviewRuntimeAPI.getPaper(paperId)
      .then((data) => {
        if (cancelled) return;
        setPaper(data);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus(statusFor(err));
      });
    return () => { cancelled = true; };
  }, [paperId]);

  const sittings = useMemo(() => {
    if (!paper) return [];
    const totalSessions = (paper.sessions || []).length || 1;
    return (paper.sessions || []).map((session) => {
      const questions = (paper.questions || [])
        .filter((q) => q.session_id === session.session_id);
      const untimed = ['Medicine', 'Dentistry'].includes(paper.metadata.subject_name);
      const minutes = untimed ? 0 : Math.round((paper.metadata.duration_minutes || 60) / totalSessions);
      return { session, questions, minutes };
    });
  }, [paper]);

  const base = `/interviews/${workspaceSlug}/${leafKey}/${paperId}`;
  const writeUrl = (session) => `${base}/write/${session.session_id}`;

  const openResult = (session, page) => {
    const saved = loadResult(userId, paperId, session.session_id);
    navigate(`${base}/${page}`, { state: { runIds: saved?.runIds || [], mode: 'written' } });
  };

  const startAgain = (session) => {
    clearDraft(userId, paperId, session.session_id);
    clearResult(userId, paperId, session.session_id);
    setRefresh((n) => n + 1);
    navigate(writeUrl(session));
  };

  if (status === 'loading') {
    return <div className="ib"><div className="ib__state">Loading…</div></div>;
  }
  if (status === 'upgrade') {
    return <div className="ib"><InterviewUpgradeGate code={gateCode} paid={holdsPaidExam(user)} /></div>;
  }
  if (status === 'error' || !paper) {
    return (
      <div className="ib">
        <div className="ib__state ib__state--error">
          Could not load this paper. <Link to={`/interviews/${workspaceSlug}/${leafKey}`}>Back</Link>
        </div>
      </div>
    );
  }

  const { metadata } = paper;

  return (
    <div className="ib">
      <div className="ib__crumbs">
        <Link to="/apps">Apps</Link> / <Link to={`/interviews/${workspaceSlug}`}>{workspaceSlug.charAt(0).toUpperCase() + workspaceSlug.slice(1)}</Link> / <Link to={`/interviews/${workspaceSlug}/${leafKey}`}>{metadata.institution}</Link>
      </div>
      <h1 className="ib__title">{metadata.title}</h1>
      <p className="ib__lede">
        {metadata.institution} · {metadata.subject_name}
        {metadata.difficulty ? ` · ${metadata.difficulty}` : ''}
      </p>

      <p className="ib__note">
        A written paper. You type your answers and each one is marked when you submit it.
        Live interviews are on the <Link to={`/interviews/${workspaceSlug}/${leafKey}`}>course page</Link>.
      </p>

      <div className="ib__session-list">
        {sittings.map(({ session, questions, minutes }) => (
          <div className="ib__session" key={session.session_id}>
            <div className="ib__session-info">
              <span className="ib__session-title">
                {session.title || `Session ${session.session_number}`}
              </span>
              <span className="ib__session-meta">
                {questions.length} question{questions.length === 1 ? '' : 's'}{minutes ? ` · about ${minutes} min` : ''}
              </span>
              {session.focus && <span className="ib__session-focus">{session.focus}</span>}
            </div>
            <SessionActions
              progress={writtenProgress(userId, paperId, session.session_id)}
              disabled={!questions.length}
              onStart={() => navigate(writeUrl(session))}
              onFeedback={() => openResult(session, 'feedback')}
              onReview={() => openResult(session, 'review')}
              onStartAgain={() => startAgain(session)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function SessionActions({ progress, disabled, onStart, onFeedback, onReview, onStartAgain }) {
  if (progress === 'marked') {
    return (
      <div className="ib__session-actions">
        <button type="button" className="ib__btn" onClick={onFeedback}>See your feedback</button>
        <button type="button" className="ib__link-btn" onClick={onReview}>Review model answers</button>
        <button type="button" className="ib__link-btn" onClick={onStartAgain}>Take it again</button>
      </div>
    );
  }
  if (progress === 'draft') {
    return (
      <div className="ib__session-actions">
        <button type="button" className="ib__btn" onClick={onStart}>Resume paper</button>
        <button type="button" className="ib__link-btn" onClick={onStartAgain}>Start again</button>
      </div>
    );
  }
  return (
    <button type="button" className="ib__btn" disabled={disabled} onClick={onStart}>
      Start written paper
    </button>
  );
}
